"use client";

import clsx from "clsx";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAlternateLinks } from "@/hooks/useAlternateLinks";
import { useLocalePath } from "@/hooks/useLocalePath";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/utils/library/languages";
import { getEnglishPath, getTranslatedPath } from "@/utils/routes";
import { useRoutedGailAppUrl } from "@/providers/AppLinkRoutingProvider";

/**
 * Translate only the first path segment (the top-level route) for cross-locale
 * language switching. Deeper segments (blog slugs, category prefixes, etc.) are
 * preserved as-is because they use their own translation systems and the
 * hreflang metadata handles the correct per-language URLs when available.
 */
function translateFirstSegment(
  pathWithoutLocale: string,
  fromLang: string,
  toLang: string,
): string {
  const segments = pathWithoutLocale.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  const first = segments[0];
  const englishKey = getEnglishPath(first, fromLang).slice(1);
  const translated = getTranslatedPath(`/${englishKey}`, toLang);
  return segments.length === 1
    ? translated
    : `${translated}/${segments.slice(1).join("/")}`;
}
import DemoButtonText from "./DemoButtonText";
import { useDemoButtonLink } from "@/hooks/useDemoButtonText";
import GailIcon from "../GailIcon";
import Button from "../library/Button";
import { ICON_SIZES, Icon } from "../library/Icon";
import { BASE_URL } from "@/utils/baseUrl";
import ShareButtons from "./ShareButtons";

type FooterProps = {
  footerDemoBtnPushToTop?: boolean;
};

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
  fr: "Français",
  ja: "日本語",
  de: "Deutsch",
};

const Footer = ({ footerDemoBtnPushToTop = false }: FooterProps) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const demoLink = useDemoButtonLink(lp("/contact-sales"));
  const dashboardHref = useRoutedGailAppUrl(
    process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#",
  );

  const pathSegments = pathname.split("/");
  const segment = pathSegments[1];
  const hasLocaleSegment = (SUPPORTED_LANGUAGES as readonly string[]).includes(
    segment,
  );
  const currentLang = hasLocaleSegment
    ? (segment as SupportedLanguage)
    : DEFAULT_LANGUAGE;
  const pathWithoutLocale = hasLocaleSegment
    ? `/${pathSegments.slice(2).join("/")}`
    : pathname;
  const queryString = searchParams.toString();
  const alternates = useAlternateLinks();
  const localizedLanguageLinks = SUPPORTED_LANGUAGES.map((lang) => {
    const alternatePath = alternates.get(lang);
    let localizedPath: string;
    if (alternatePath) {
      localizedPath = alternatePath;
    } else if (pathWithoutLocale === "/") {
      localizedPath = `/${lang}`;
    } else {
      const translatedForTarget = translateFirstSegment(
        pathWithoutLocale,
        currentLang,
        lang,
      );
      localizedPath = `/${lang}${translatedForTarget}`;
    }

    return {
      lang,
      href: queryString ? `${localizedPath}?${queryString}` : localizedPath,
      nativeLabel: LANGUAGE_LABELS[lang],
    };
  });

  const LINKS = [
    { href: lp("/product"), label: t("header.product") },
    { href: lp("/pricing"), label: t("header.pricing") },
    { href: lp("/compliance"), label: t("header.compliance") },
    { href: lp("/blog"), label: t("header.blog") },
    { href: lp("/careers"), label: t("header.careers") },
    { href: lp("/privacy-policy"), label: t("header.privacyPolicy") },
    { href: lp("/terms"), label: t("header.termsCondition") },
    {
      href: "https://trust.meetgail.com",
      label: t("header.trustCenter"),
      target: "_blank",
    },
    {
      href: dashboardHref,
      label: t("header.signIn"),
      className: "mt-6",
    },
  ];

  return (
    <footer className={clsx("bg-gail-primary pb-16")}>
      <div className={clsx("mx-auto max-w-8xl")}>
        <div
          className={clsx(
            "grid",
            "grid-cols-1",
            "lg:grid-cols-[1fr_200px]",
            "gap-4",
            "my-8 lg:mt-12",
            "lg:items-center",
            "px-4",
          )}
        >
          <div
            className={clsx(
              "mt-8 md:mt-0",
              "flex",
              "flex-col lg:flex-row",
              "text-4xl md:text-3xl xl:text-4xl",
              "font-medium",
              "text-center lg:text-left",
              "leading-none",
            )}
          >
            <h2 className={clsx("text-white")}>
              <a href={lp("/contact-sales")}>{t("footer.tryGailFree")}</a>
            </h2>
            <h2 className={clsx("text-[#b9d4ff]")}>
              {t("footer.transformative")}
            </h2>
          </div>
          <div className={clsx("mb-12 md:mb-0")}>
            <Button
              type="tertiary"
              compact
              href={footerDemoBtnPushToTop ? undefined : demoLink.href}
              target={footerDemoBtnPushToTop ? undefined : demoLink.target}
              onClick={
                footerDemoBtnPushToTop
                  ? () => {
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                  : undefined
              }
              className="mx-auto py-3 w-full max-w-2xs"
            >
              <DemoButtonText fallback={t("footer.requestDemo")} />
            </Button>
          </div>
        </div>
        <hr className={clsx("mx-4 md:mx-0 border-[#4190df]")} />
        <div
          className={clsx(
            "flex",
            "flex-row",
            "justify-between",
            "items-start",
            "pt-8",
          )}
        >
          <div className={clsx("ml-4", "flex", "flex-col", "gap-4")}>
            <GailIcon />
            <ShareButtons
              url={`${BASE_URL}${pathname}`}
              variant="light"
              size={28}
              utmContent="footer"
            />
          </div>
          <nav className={clsx("flex", "flex-col", "max-w-1/2", "px-2")}>
            {LINKS.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                target={link.target}
                rel={
                  link.target === "_blank" ? "noopener noreferrer" : undefined
                }
                className={clsx(
                  "text-white",
                  "font-sans",
                  link.className,
                  "hover:text-[#C0C0C0]",
                )}
              >
                {link.label}
                {link.target === "_blank" && (
                  <Icon
                    icon="arrow-up-right-from-square"
                    className="ml-1"
                    size={ICON_SIZES.XS}
                  />
                )}
              </Link>
            ))}
            <div className={clsx("flex", "justify-start", "pt-6")}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Choose language"
                    className={clsx(
                      "self-start",
                      "inline-flex",
                      "items-center",
                      "gap-2",
                      "rounded-md",
                      "border",
                      "border-[#4190df]",
                      "bg-white/5",
                      "px-3",
                      "py-2",
                      "text-white",
                      "font-sans",
                      "text-sm",
                      "transition-colors",
                      "hover:bg-white/10",
                      "focus-visible:outline-none",
                      "focus-visible:ring-2",
                      "focus-visible:ring-[#6babff]",
                    )}
                  >
                    <Icon
                      icon="globe"
                      size={ICON_SIZES.SM}
                      className="opacity-90"
                    />
                    <span>{LANGUAGE_LABELS[currentLang]}</span>
                    <Icon icon="chevron-down" size={ICON_SIZES.XS} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className={clsx("w-56", "max-h-72", "overflow-y-auto")}
                >
                  {localizedLanguageLinks.map((languageLink) => (
                    <DropdownMenuItem key={languageLink.lang} asChild>
                      <a
                        href={languageLink.href}
                        lang={languageLink.lang}
                        hrefLang={languageLink.lang}
                        aria-current={
                          languageLink.lang === currentLang ? "page" : undefined
                        }
                        className={clsx(
                          "font-sans",
                          "text-white",
                          "hover:text-white",
                          languageLink.lang === currentLang && "text-[#6babff]",
                        )}
                      >
                        {languageLink.nativeLabel}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className={clsx("flex", "justify-end", "py-8")}>
              <p className={clsx("text-[10px]", "text-white")}>
                {t("footer.copyright")}
              </p>
            </div>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
